import { Suspense, useEffect, useState, type ReactNode } from 'react';
import { NavLink, useLocation } from 'react-router';
import { useQuery } from '@tanstack/react-query';
import { Bell, LogOut, Menu, WifiOff } from 'lucide-react';
import { useAuth } from '../lib/auth';
import { api } from '../lib/api';
import { ROLE_LABELS } from '../lib/types';
import { PAGES, type PageDef } from '../modules';
import { Badge, Button, LoadingPage } from './ui';

export interface Alert { level: 'info' | 'warn' | 'crit'; message: string; link?: string }

function useOnline() {
  const [online, setOnline] = useState(typeof navigator === 'undefined' ? true : navigator.onLine);
  useEffect(() => {
    const on = () => setOnline(true), off = () => setOnline(false);
    window.addEventListener('online', on);
    window.addEventListener('offline', off);
    return () => { window.removeEventListener('online', on); window.removeEventListener('offline', off); };
  }, []);
  return online;
}

export function visiblePages(can: (p: PageDef['any'][number]) => boolean): PageDef[] {
  return PAGES.filter((p) => p.any.length === 0 || p.any.some((x) => can(x)));
}

export function Layout({ children, page }: { children: ReactNode; page?: PageDef }) {
  const { me, can, logout, setting } = useAuth();
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const online = useOnline();
  useEffect(() => setOpen(false), [location.pathname]);

  const alerts = useQuery({
    queryKey: ['alerts'],
    queryFn: () => api.get<Alert[]>('/reports/alerts'),
    enabled: can('reports.view'),
    refetchInterval: 120_000,
    retry: false,
  });
  const alertCount = (alerts.data ?? []).filter((a) => a.level !== 'info').length;

  const pages = visiblePages(can);
  const main = pages.filter((p) => p.section === 'main');
  const admin = pages.filter((p) => p.section === 'admin');
  const account = pages.filter((p) => p.section === 'account');
  const shopName = setting<string>('shop.name') || 'Kiosk Kasse';

  const navItem = (p: PageDef) => (
    <NavLink key={p.path} to={p.path} end={p.path === '/'} className={({ isActive }) => `nav${isActive ? ' active' : ''}`}>
      <p.icon size={18} />
      <span>{p.label}</span>
      {p.path === '/' && alertCount > 0 && <Badge tone="crit">{alertCount}</Badge>}
      {p.badge && <p.badge variant="nav" />}
    </NavLink>
  );

  return (
    <div className="app">
      {open && <div className="scrim" onClick={() => setOpen(false)} />}
      <aside className={`sidebar${open ? ' open' : ''}`} aria-label="Navigation">
        <div className="brand"><img src="/icon.svg" alt="" /> <span>{shopName}</span></div>
        {main.map(navItem)}
        {admin.length > 0 && <div className="nav-section">Verwaltung</div>}
        {admin.map(navItem)}
        <div className="spacer" />
        {account.map(navItem)}
        <div className="user-box">
          <div style={{ color: '#fff', fontWeight: 600 }}>{me?.user.displayName}</div>
          <div style={{ color: '#7f8899', marginBottom: 8 }}>{me ? ROLE_LABELS[me.user.role] : ''}</div>
          <Button size="sm" variant="ghost" style={{ color: 'var(--sidebar-text)', paddingLeft: 0 }} icon={<LogOut size={16} />} onClick={() => void logout()}>
            Abmelden
          </Button>
        </div>
      </aside>
      <div className="main">
        <header className="topbar">
          <Button variant="ghost" iconOnly aria-label="Menü" onClick={() => setOpen(true)} style={{ color: '#fff' }}><Menu /></Button>
          <strong className="grow">{page?.label ?? shopName}</strong>
          {pages.map((p) => (p.badge && p !== page ? <p.badge key={p.path} variant="topbar" /> : null))}
          {alertCount > 0 && <NavLink to="/" style={{ color: '#fff' }}><Bell size={20} /> <Badge tone="crit">{alertCount}</Badge></NavLink>}
        </header>
        {!online && (
          <div className="banner warn" style={{ margin: 0, borderRadius: 0 }}>
            <WifiOff size={18} /> Keine Internetverbindung – die Kasse arbeitet offline weiter, Daten werden später übertragen.
          </div>
        )}
        <main className={`content${page?.fullWidth ? ' full' : ''}`}>
          <Suspense fallback={<LoadingPage />}>{children}</Suspense>
        </main>
      </div>
    </div>
  );
}
